import { useContext, useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import highchartsMore from "highcharts/highcharts-more";

import { i18n } from "../../../translate/i18n";

import PropTypes from 'prop-types';
import { TenantContext } from "../../../templates/context/TenantContext";
import { currencyFormatter, createIntFormatter } from "../../../utils/formatter/formatter";

highchartsMore(Highcharts);

export function AreaRangeChart({ xLabels, ranges, lines, colors, legendOrderLeft }) {

  const { t } = i18n;

  const { unit } = useContext(TenantContext);

  const [seriesVisibility, setSeriesVisibility] = useState(() =>
    Object.fromEntries(lines.map(line => [line.label, line.visible ?? true]))
  );

  useEffect(() => {
    setSeriesVisibility(prev => {
      const next = { ...prev };
      lines.forEach(line => {
        if (next[line.label] === undefined) {
          next[line.label] = line.visible ?? true;
        }
      });
      return next;
    });
  }, [lines]);

  useEffect(() => {
    const urlHash = window.location.hash;
    if (urlHash) {
      const sectionId = urlHash.substring(1);

      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, []);

  const options = useMemo(() => {
    const integerRanges = ranges.map(range => {
      return range.map(r => {
        if (typeof r === "number") return Math.ceil(r);
        else return r;
      });
    });

    const linesData = lines.map(line => {
      return {
        name: line.label,
        data: line.values,
        zIndex: 1,
        color: line.color,
        lineColor: line.color,
        dashStyle: line.lineStyle,
        visible: seriesVisibility[line.label] !== undefined
          ? seriesVisibility[line.label]
          : line.visible,
        connectNulls: true,
        marker: {
          enabled: line.marker ?? false,
          fillColor: "white",
          lineWidth: 2,
          lineColor: line.color,
        },
      };
    });

    function formatYAxis() {
      const max = this.axis.max;
      const value = this.value;

      if (value === 0) {
        return 0;
      } else if (max > 1000000000) {
        return value / 1000000000 + "bi";
      } else if (max > 1000000) {
        return value / 1000000 + "M";
      } else if (max > 1000) {
        return value / 1000 + "k";
      } else {
        return value;
      }
    }

    return {
      chart: {
        height: "70%",
      },
      colors: colors,
      title: {
        text: "",
      },
      xAxis: {
        categories: xLabels,
      },
      legend: legendOrderLeft
        ? {
            align: "left",
            itemDistance: 5,
            padding: 0,
            margins: 0,
          }
        : {},
      yAxis: {
        title: {
          text: null,
        },
        labels: {
          formatter: formatYAxis,
        },
      },
      tooltip: {
        crosshairs: true,
        shared: true,
        formatter: function () {
          const points = this.points;

          let html = `<b>${points[0].x}</b><br/>`;

          const formatNumber = (value) => {
            if (unit === "items") {
              return createIntFormatter().format(value);
            } else {
              return currencyFormatter.format(value);
            }
          };

          points.forEach(function (point) {
            // ponto artificial de conexão entre o histórico realizado e as linhas de previsão;
            // não representa um dado real, então não deve aparecer no tooltip
            const isConnectorPoint = point.point.options?.marker?.enabled === false;
            if (isConnectorPoint) return;

            if (point.series.name === "Demanda do Cliente") {
              html += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>` +
                formatNumber(point.point.options.low) + "</b> - <b>" +
                formatNumber(point.point.options.high) + "</b><br/>";
            } else {
              html += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>` +
                formatNumber(point.y) + "</b><br/>";
            }
          });

          return html;
        }
      },
      scrollbar: {
        enabled: true,
      },
      series: [
        {
          name: t("customerDemand"),
          data: integerRanges,
          type: "arearange",
          lineWidth: 0,
          color: "#0fd17c65",
          fillOpacity: 0.3,
          zIndex: 0,
          connectNulls: true,
          marker: {
            enabled: false,
          },
          visible: seriesVisibility[t("customerDemand")] !== undefined
            ? seriesVisibility[t("customerDemand")]
            : true,
        },
        ...linesData,
      ],
      plotOptions: {
        series: {
          connectNulls: true,
          events: {
            hide: function () {
              setSeriesVisibility(prev => ({ ...prev, [this.name]: false }));
            },
            show: function () {
              setSeriesVisibility(prev => ({ ...prev, [this.name]: true }));
            },
          },
        },
        arearange: {
          connectNulls: true,
        },
      },
    };
  }, [xLabels, ranges, lines, colors, legendOrderLeft, unit, seriesVisibility, t]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

AreaRangeChart.propTypes = {
  xLabels: PropTypes.arrayOf(PropTypes.string),
  ranges: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf([null])
  ]))),
  lines: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string,
    label: PropTypes.string,
    lineStyle: PropTypes.string,
    marker: PropTypes.bool,
    name: PropTypes.func,
    values: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.oneOf([null])]))
  })),
  colors: PropTypes.arrayOf(PropTypes.string),
  legendOrderLeft: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([null])
  ])
}